import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { ApiError } from '../../api/client'
import { usePollAktion } from '../../hooks/queries'
import { XIcon } from '../../components/Icons'

/** Dieselben Zahlen wie im Server (`PollsController`). */
const MIN_ANTWORTEN = 2
const MAX_ANTWORTEN = 8

/**
 * Eine Abstimmung anlegen (#56).
 *
 * **Zwei leere Felder stehen von Anfang an da.** Eine Abstimmung braucht
 * mindestens zwei Antworten; erst „Antwort hinzufügen" tippen zu müssen, um
 * überhaupt eine zu bekommen, wäre ein Griff für nichts. Weitere kommen mit
 * einem Knopf dazu, und leere fallen beim Senden weg — wer drei ausfüllt und
 * das vierte stehen lässt, hat nichts falsch gemacht.
 *
 * **Höchstens acht, und der Grund steht in der Bedienung**, nicht im
 * Speicher: eine Liste, die zum Rollfeld wird, ist keine Frage mehr, sondern
 * ein Formular. Dieselbe Grenze hält der Server.
 *
 * **Am Seitenkörper** (`createPortal`), wie jedes Blatt hier — sonst klebt es
 * am Verlauf und scrollt mit.
 */
export function Abstimmungsformular({ tripId, onZu }: { tripId: number; onZu: () => void }) {
  const aktion = usePollAktion(tripId)
  const [frage, setFrage] = useState('')
  const [antworten, setAntworten] = useState<string[]>(['', ''])
  const [multi, setMulti] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    const taste = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onZu()
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [onZu])

  const gefuellt = antworten.map((a) => a.trim()).filter((a) => a !== '')
  // **Der Knopf sagt vorher, ob er kann.** Eine Absage nach dem Tippen wäre
  // die stille Absage mit Umweg; hier steht der Grund daneben, solange er gilt.
  const bereit = frage.trim() !== '' && new Set(gefuellt).size >= MIN_ANTWORTEN

  function setzeAntwort(i: number, wert: string): void {
    setAntworten((alt) => alt.map((a, j) => (j === i ? wert : a)))
  }

  async function sende(): Promise<void> {
    setProblem(null)
    try {
      await aktion.mutateAsync({
        art: 'anlegen',
        question: frage.trim(),
        options: antworten,
        multi,
      })
      onZu()
    } catch (fehler) {
      setProblem(
        fehler instanceof ApiError && fehler.offline
          ? 'Dafür braucht es Netz. Versuch es später noch einmal.'
          : fehler instanceof Error
            ? fehler.message
            : 'Das hat nicht geklappt.',
      )
    }
  }

  return createPortal(
    <div
      className="bogen bogen--abstimmung"
      role="dialog"
      aria-modal="true"
      aria-label="Abstimmung anlegen"
    >
      <div className="bogen__blatt">
        <div className="bogen__kopf">
          <h3 className="bogen__titel">Abstimmung</h3>
          <button type="button" className="icon-button" onClick={onZu} aria-label="Schließen">
            <XIcon size={20} />
          </button>
        </div>

        <label className="field">
          <span className="field__label">Frage</span>
          <input
            className="field__input"
            type="text"
            maxLength={200}
            placeholder="Wohin morgen?"
            value={frage}
            onChange={(event) => setFrage(event.target.value)}
          />
        </label>

        <span className="field__label">Antworten</span>
        {antworten.map((wert, i) => (
          <div key={i} className="row" style={{ gap: 8, marginBottom: 8 }}>
            <input
              className="field__input"
              type="text"
              maxLength={120}
              placeholder={i === 0 ? 'Berge' : i === 1 ? 'Meer' : 'Noch etwas'}
              value={wert}
              onChange={(event) => setzeAntwort(i, event.target.value)}
              style={{ flex: '1 1 auto' }}
            />
            {/* Wegnehmen erst ab der dritten: unter zwei wäre es keine
                Abstimmung mehr, und ein Knopf, der nichts darf, ist einer zu
                viel. */}
            {antworten.length > MIN_ANTWORTEN && (
              <button
                type="button"
                className="icon-button"
                aria-label={`Antwort ${i + 1} entfernen`}
                onClick={() => setAntworten((alt) => alt.filter((_, j) => j !== i))}
              >
                <XIcon size={18} />
              </button>
            )}
          </div>
        ))}

        {antworten.length < MAX_ANTWORTEN && (
          <button
            type="button"
            className="button button--small button--outline"
            onClick={() => setAntworten((alt) => [...alt, ''])}
          >
            Antwort hinzufügen
          </button>
        )}

        <label className="row" style={{ gap: 8, alignItems: 'center', marginTop: 12 }}>
          <input
            type="checkbox"
            checked={multi}
            onChange={(event) => setMulti(event.target.checked)}
          />
          <span>Mehrfachauswahl erlauben</span>
        </label>

        {/* **Was die Mitlesenden sehen werden, steht hier — vor dem Senden.**
            Wer eine Abstimmung stellt, soll wissen, was er dabei preisgibt und
            was nicht. Diese Zeile ist kein Beiwerk: sie ist die Zusage, die
            die Anwendung an anderer Stelle einhält. */}
        <p className="feld__hinweis" style={{ marginTop: 12 }}>
          Alle in dieser Reise können abstimmen. Sie sehen danach nur die
          Prozente — keine Stimmenzahl und keine Namen. <strong>Wer wie
          abgestimmt hat, siehst nur du.</strong>
        </p>

        {problem && (
          <p className="feld__hinweis" role="alert">
            {problem}
          </p>
        )}

        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button
            type="button"
            className="button"
            disabled={!bereit || aktion.isPending}
            onClick={() => void sende()}
          >
            {aktion.isPending ? 'Einen Moment …' : 'Abstimmung senden'}
          </button>
          <button type="button" className="button button--outline" onClick={onZu}>
            Abbrechen
          </button>
        </div>

        {!bereit && (
          <p className="feld__hinweis">
            Es braucht eine Frage und zwei verschiedene Antworten.
          </p>
        )}
      </div>
    </div>,
    document.body,
  )
}
